/**
 * Splits markdown into prose blocks and GFM tables.
 *
 * The Word and PDF exporters used to flatten every table row into a run of text joined by spaces or
 * tabs, which turned generated frequency tables into unreadable paragraphs. They now render real
 * tables, and both need the same parsing, so it lives here.
 */

const isRow = (line) => line.startsWith('|') && line.endsWith('|')

// A GFM delimiter row: |---|:--:|---| (dashes, colons, spaces and pipes only, at least one dash).
const isDelimiter = (line) => isRow(line) && /^\|[\s:|-]*-[\s:|-]*\|$/.test(line)

const splitRow = (line) =>
    line
        .slice(1, -1)
        .split('|')
        .map((cell) => stripInlineMarkdown(cell.trim()))

/** Strip the inline syntax exporters can't render, keeping the text itself. */
export const stripInlineMarkdown = (text) =>
    String(text)
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\*(.*?)\*/g, '$1')
        .replace(/`(.*?)`/g, '$1')
        .trim()

/**
 * @param {string} content markdown
 * @returns {Array<{type:'text',content:string}|{type:'table',head:string[],rows:string[][]}>}
 */
export function parseMarkdownBlocks(content) {
    const lines = String(content ?? '').split('\n')
    const blocks = []
    let text = []

    const flushText = () => {
        const joined = text.join('\n').trim()
        if (joined) {
            blocks.push({ type: 'text', content: joined })
        }
        text = []
    }

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()
        const next = (lines[i + 1] ?? '').trim()

        // A table only starts where a header row is followed by a delimiter row; pipes on their own
        // are ordinary text and must not be swallowed.
        if (isRow(line) && isDelimiter(next)) {
            flushText()

            const head = splitRow(line)
            const rows = []
            i += 1 // consume the delimiter

            while (i + 1 < lines.length && isRow(lines[i + 1].trim())) {
                i += 1
                const row = lines[i].trim()
                if (isDelimiter(row)) continue
                rows.push(splitRow(row))
            }

            // Generated tables are occasionally ragged; docx rejects rows whose cell count differs
            // from the header, so square them off here rather than in each exporter.
            blocks.push({
                type: 'table',
                head,
                rows: rows.map((row) =>
                    Array.from({ length: head.length }, (_, c) => row[c] ?? '')
                ),
            })
            continue
        }

        text.push(lines[i])
    }

    flushText()

    return blocks
}
